import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit, X, Save, Phone, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const ROLE_LABEL = {
  coordinator: "Coordinador",
  director_deportivo: "Director Deportivo",
  coach: "Entrenador",
  delegado: "Delegado",
  club_admin: "Admin Club",
};

const TEAM_STAFF_ROLES = ["coach", "delegado"];

export default function ClubStaff() {
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "coach", phone: "", team_ids: [] });

  const load = async () => {
    try {
      const [u, t] = await Promise.all([api.get("/users"), api.get("/teams")]);
      setUsers(u.data);
      setTeams(t.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: form.name,
        email: form.email,
        password: form.password,
        role: form.role,
        phone: form.phone || null,
        team_ids: form.team_ids,
      };
      await api.post("/auth/register", payload);
      toast.success("Usuario añadido");
      setShowForm(false);
      setForm({ name: "", email: "", password: "", role: "coach", phone: "", team_ids: [] });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este usuario? Se quitará también de los equipos donde estaba asignado.")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Usuario eliminado");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const teamName = (id) => teams.find(t => t.id === id)?.name || "—";

  return (
    <div>
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="overline">Personal técnico</div>
          <h1 className="font-heading font-black text-5xl tracking-tighter uppercase mt-2">Staff</h1>
          <p className="text-muted-foreground text-sm mt-2">Coordinadores, entrenadores y delegados del club. Edita el teléfono y los equipos asignados desde la ficha de cada uno.</p>
        </div>
        <button data-testid="new-user-btn" onClick={() => setShowForm(true)} className="btn-volt">
          <Plus size={16} /> Añadir usuario
        </button>
      </div>

      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="border-b border-border">
            <tr className="text-left overline">
              <th className="p-3">Nombre</th>
              <th className="p-3">Email</th>
              <th className="p-3">Teléfono</th>
              <th className="p-3">Rol</th>
              <th className="p-3">Equipos</th>
              <th className="p-3 w-24 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-border/60 hover:bg-white/[0.02] transition-colors">
                <td className="p-3 font-heading font-bold uppercase tracking-tight">{u.name}</td>
                <td className="p-3 font-mono text-xs text-muted-foreground">{u.email}</td>
                <td className="p-3 font-mono text-xs">
                  {u.phone ? (
                    <a href={`tel:${u.phone}`} className="inline-flex items-center gap-1 hover:text-primary" data-testid={`phone-${u.id}`}>
                      <Phone size={11} /> {u.phone}
                    </a>
                  ) : <span className="text-muted-foreground/60">—</span>}
                </td>
                <td className="p-3">
                  <span className="px-2 py-1 border border-border font-mono text-xs">{ROLE_LABEL[u.role] || u.role}</span>
                </td>
                <td className="p-3">
                  {(u.assigned_teams && u.assigned_teams.length > 0) ? (
                    <div className="flex flex-wrap gap-1" data-testid={`teams-${u.id}`}>
                      {u.assigned_teams.map(t => (
                        <span key={t.id} className="px-1.5 py-0.5 border border-primary/40 text-primary text-[10px] font-mono">{t.name}</span>
                      ))}
                    </div>
                  ) : TEAM_STAFF_ROLES.includes(u.role)
                    ? <span className="text-muted-foreground/60 text-xs">Sin equipos</span>
                    : <span className="text-muted-foreground/40 text-xs">—</span>}
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  {u.role !== "super_admin" && (
                    <button data-testid={`edit-user-${u.id}`} onClick={() => setEditing(u)} className="text-muted-foreground hover:text-primary mr-2" title="Editar"><Edit size={15} /></button>
                  )}
                  {u.role !== "super_admin" && u.role !== "club_admin" && (
                    <button data-testid={`del-user-${u.id}`} onClick={() => remove(u.id)} className="text-muted-foreground hover:text-destructive" title="Eliminar"><Trash2 size={15} /></button>
                  )}
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-muted-foreground text-sm">Sin usuarios aún.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <form onSubmit={submit} className="bg-card border border-border w-full max-w-md p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between">
              <div>
                <div className="overline">Añadir</div>
                <div className="font-heading font-black text-2xl uppercase tracking-tight">Nuevo usuario</div>
              </div>
              <button type="button" onClick={() => setShowForm(false)} className="p-1 hover:text-primary"><X size={18} /></button>
            </div>
            <FormRow label="Nombre *"><input required data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full h-10 bg-transparent border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none" /></FormRow>
            <FormRow label="Email *"><input type="email" required data-testid="user-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full h-10 bg-transparent border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none" /></FormRow>
            <FormRow label="Contraseña *"><input type="password" required minLength={6} data-testid="user-pass-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full h-10 bg-transparent border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none" /></FormRow>
            <FormRow label="Teléfono">
              <input type="tel" data-testid="user-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+34 600 000 000" className="w-full h-10 bg-transparent border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none" />
            </FormRow>
            <FormRow label="Rol *">
              <select required data-testid="user-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full h-10 bg-card border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none">
                <option value="club_admin">Administrador</option>
                <option value="director_deportivo">Director Deportivo</option>
                <option value="coordinator">Coordinador</option>
                <option value="coach">Entrenador</option>
                <option value="delegado">Delegado</option>
              </select>
            </FormRow>
            <FormRow label="Equipos asignados">
              <TeamPicker teams={teams} selected={form.team_ids} onChange={(ids) => setForm({ ...form, team_ids: ids })} testidPrefix="new" />
            </FormRow>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="h-10 px-5 border border-border text-sm uppercase font-heading tracking-wider">Cancelar</button>
              <button type="submit" data-testid="submit-user-btn" className="btn-volt">Crear</button>
            </div>
          </form>
        </div>
      )}

      {editing && (
        <EditUserModal
          user={editing}
          teams={teams}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function EditUserModal({ user, teams, onClose, onSaved }) {
  const { user: me } = useAuth();
  const [name, setName] = useState(user.name || "");
  const [email, setEmail] = useState(user.email || "");
  const [phone, setPhone] = useState(user.phone || "");
  const [role, setRole] = useState(user.role);
  const [newPass, setNewPass] = useState("");
  const [teamIds, setTeamIds] = useState(user.team_ids || []);
  const [saving, setSaving] = useState(false);
  const canAdmin = ["club_admin", "super_admin"].includes(me?.role);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { name, phone: phone || null, team_ids: teamIds };
      if (email && email.trim().toLowerCase() !== (user.email || "").toLowerCase()) {
        payload.email = email.trim().toLowerCase();
      }
      if (canAdmin && role !== user.role) payload.role = role;
      if (canAdmin && newPass) payload.password = newPass;
      await api.put(`/users/${user.id}`, payload);
      toast.success("Usuario actualizado");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-card border border-border w-full max-w-md p-6 space-y-4 max-h-[90vh] overflow-y-auto" data-testid="edit-user-form">
        <div className="flex items-start justify-between">
          <div>
            <div className="overline">Editar</div>
            <div className="font-heading font-black text-2xl uppercase tracking-tight">{user.name}</div>
            <div className="text-xs font-mono text-muted-foreground mt-1">{ROLE_LABEL[user.role] || user.role}</div>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:text-primary"><X size={18} /></button>
        </div>
        <FormRow label="Nombre *"><input required data-testid="edit-name-input" value={name} onChange={(e) => setName(e.target.value)} className="w-full h-10 bg-transparent border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none" /></FormRow>
        <FormRow label="Email *">
          <input type="email" required data-testid="edit-email-input" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full h-10 bg-transparent border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none" />
          <div className="text-[10px] text-muted-foreground mt-1">Corrige aquí si el email inicial era un placeholder. El usuario podrá recibir notificaciones a esta nueva dirección.</div>
        </FormRow>
        <FormRow label="Teléfono">
          <input type="tel" data-testid="edit-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+34 600 000 000" className="w-full h-10 bg-transparent border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none" />
        </FormRow>
        {canAdmin && (
          <FormRow label="Rol">
            <select data-testid="edit-role-select" value={role} onChange={(e) => setRole(e.target.value)} className="w-full h-10 bg-card border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none">
              <option value="club_admin">Administrador</option>
              <option value="director_deportivo">Director Deportivo</option>
              <option value="coordinator">Coordinador</option>
              <option value="coach">Entrenador</option>
              <option value="delegado">Delegado</option>
            </select>
          </FormRow>
        )}
        {canAdmin && (
          <FormRow label="Nueva contraseña">
            <input type="password" minLength={6} data-testid="edit-pass-input" value={newPass} onChange={(e) => setNewPass(e.target.value)} placeholder="Dejar vacío para no cambiarla" className="w-full h-10 bg-transparent border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none" />
          </FormRow>
        )}
        <FormRow label={<span className="inline-flex items-center gap-1"><Users size={11} /> Equipos asignados</span>}>
          <TeamPicker teams={teams} selected={teamIds} onChange={setTeamIds} testidPrefix="edit" />
        </FormRow>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="h-10 px-5 border border-border text-sm uppercase font-heading tracking-wider">Cancelar</button>
          <button type="submit" data-testid="save-user-btn" disabled={saving} className="btn-volt disabled:opacity-50"><Save size={14} /> {saving ? "Guardando…" : "Guardar"}</button>
        </div>
      </form>
    </div>
  );
}

function TeamPicker({ teams, selected, onChange, testidPrefix = "team" }) {
  const toggle = (id) => {
    const next = selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id];
    onChange(next);
  };
  const groups = useMemo(() => {
    const byCat = {};
    teams.forEach(t => { (byCat[t.category_id || "otros"] ||= []).push(t); });
    return byCat;
  }, [teams]);

  if (teams.length === 0) {
    return <div className="text-xs text-muted-foreground p-3 border border-dashed border-border">Aún no hay equipos en el club.</div>;
  }
  return (
    <div className="border border-border max-h-56 overflow-y-auto divide-y divide-border/40" data-testid={`${testidPrefix}-team-picker`}>
      {Object.entries(groups).map(([cat, list]) => (
        <div key={cat} className="p-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
            {list.map(t => {
              const active = selected.includes(t.id);
              return (
                <label key={t.id} data-testid={`${testidPrefix}-team-opt-${t.id}`} className={`flex items-center gap-2 px-2 py-1.5 cursor-pointer text-xs border ${active ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/40"}`}>
                  <input type="checkbox" checked={active} onChange={() => toggle(t.id)} className="accent-primary" />
                  <span className="font-mono truncate">{t.name}</span>
                </label>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function FormRow({ label, children }) {
  return (
    <div>
      <label className="overline block mb-1">{label}</label>
      {children}
    </div>
  );
}
